/**
 * Boards per space (intelligence module). Same shape pattern as
 * WatchListContext — loaded once at the dashboard level, optimistic
 * mutations from anywhere in the app.
 *
 * Storage lives in Mongo's `boards` collection (reusing v1 boards), with
 * the-core ad IDs in a parallel `theCoreAdIds` array and saved landing
 * pages in `theCoreLandingPageIds`. The provider surfaces:
 *   - boards: [{id, name, description, ad_ids, ad_count, landing_page_ids, landing_page_count, ...}]
 *   - boardsContainingAd(adId): id[]   ← fast O(boards) lookup
 *   - boardsContainingLandingPage(pageId): id[]
 *   - createBoard({name, description})
 *   - deleteBoard(boardId)
 *   - setAdBoards(adId, boardIds)       ← used by the save-to-boards picker
 *   - setLandingPageBoards(pageId, boardIds)
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import * as API from '@/services/api.js'


const BoardsContext = createContext(null)


export function BoardsProvider({ children }) {

    const { spaceId } = useParams()
    const [boards, setBoards] = useState([])
    const [isLoading, setIsLoading] = useState(false)

    const refresh = useCallback(async () => {
        if (!spaceId) {
            setBoards([])
            return
        }
        setIsLoading(true)
        try {
            const { data } = await API.listIntelligenceBoards()
            setBoards(data?.boards ?? [])
        } catch {
            setBoards([])
        } finally {
            setIsLoading(false)
        }
    }, [spaceId])

    useEffect(() => {
        refresh()
    }, [refresh])

    const boardsContainingAd = useCallback((adId) => {
        if (!adId) {
            return []
        }
        return boards.filter((b) => (b.ad_ids ?? []).includes(adId)).map((b) => b.id)
    }, [boards])

    const boardsContainingLandingPage = useCallback((pageId) => {
        if (!pageId) {
            return []
        }
        return boards.filter((b) => (b.landing_page_ids ?? []).includes(pageId)).map((b) => b.id)
    }, [boards])

    const createBoard = useCallback(async ({ name, description }) => {
        const { data } = await API.createIntelligenceBoard({ name, description })
        const newBoard = data?.board
        if (newBoard) {
            // Newest-first matches the server sort.
            setBoards((prev) => [newBoard, ...prev])
        }
        return newBoard
    }, [])

    const deleteBoard = useCallback(async (boardId) => {
        const snapshot = boards
        setBoards((prev) => prev.filter((b) => b.id !== boardId))
        try {
            await API.deleteIntelligenceBoard(boardId)
        } catch (e) {
            setBoards(snapshot)
            throw e
        }
    }, [boards])

    // Optimistic set: diff the incoming board id set against the current
    // membership for adId, patch every affected board's ad_ids array, then
    // fire the API call. On error, restore the snapshot.
    const setAdBoards = useCallback(async (adId, boardIds) => {
        if (!adId) {
            return
        }
        const included = new Set(boardIds ?? [])
        const snapshot = boards
        setBoards((prev) => prev.map((b) => {
            const has = (b.ad_ids ?? []).includes(adId)
            const shouldHave = included.has(b.id)
            if (has === shouldHave) {
                return b
            }
            const next = shouldHave
                ? [...(b.ad_ids ?? []), adId]
                : (b.ad_ids ?? []).filter((id) => id !== adId)
            return { ...b, ad_ids: next, ad_count: next.length }
        }))
        try {
            const { data } = await API.setAdIntelligenceBoards(adId, [...included])
            // Server returns the canonical post-update list — replace local
            // state so any drift (e.g. concurrent edits) gets reconciled.
            if (data?.boards) {
                setBoards(data.boards)
            }
        } catch (e) {
            setBoards(snapshot)
            throw e
        }
    }, [boards])

    // Same optimistic flow as setAdBoards, over landing_page_ids.
    const setLandingPageBoards = useCallback(async (pageId, boardIds) => {
        if (!pageId) {
            return
        }
        const included = new Set(boardIds ?? [])
        const snapshot = boards
        setBoards((prev) => prev.map((b) => {
            const has = (b.landing_page_ids ?? []).includes(pageId)
            const shouldHave = included.has(b.id)
            if (has === shouldHave) {
                return b
            }
            const next = shouldHave
                ? [...(b.landing_page_ids ?? []), pageId]
                : (b.landing_page_ids ?? []).filter((id) => id !== pageId)
            return { ...b, landing_page_ids: next, landing_page_count: next.length }
        }))
        try {
            const { data } = await API.setLandingPageIntelligenceBoards(pageId, [...included])
            if (data?.boards) {
                setBoards(data.boards)
            }
        } catch (e) {
            setBoards(snapshot)
            throw e
        }
    }, [boards])

    const value = useMemo(() => ({
        boards,
        isLoading,
        boardsContainingAd,
        boardsContainingLandingPage,
        createBoard,
        deleteBoard,
        setAdBoards,
        setLandingPageBoards,
        refresh,
    }), [boards, isLoading, boardsContainingAd, boardsContainingLandingPage, createBoard, deleteBoard, setAdBoards, setLandingPageBoards, refresh])

    return <BoardsContext.Provider value={value}>{children}</BoardsContext.Provider>
}


export function useBoards() {
    const ctx = useContext(BoardsContext)
    if (!ctx) {
        return {
            boards: [],
            isLoading: false,
            boardsContainingAd: () => [],
            boardsContainingLandingPage: () => [],
            createBoard: async () => null,
            deleteBoard: async () => {},
            setAdBoards: async () => {},
            setLandingPageBoards: async () => {},
            refresh: async () => {},
        }
    }
    return ctx
}
